import { gql, useReactiveVar, useApolloClient } from "@apollo/client";
import { activeFulfillmentIdVar, saveToFutureFulfillmentsVar, } from "../helpers/client";
import { SaveFulfillmentSettingsFragmentDoc, useUpdateFulfillmentMutation, useUpdateUserMutation, OtherUnfulfilledFulfillmentsFragmentDoc, UpdateUserFulfillmentAddressFragmentDoc, ChooseSubscriptionScreenVariantFragmentDoc, UpdateVariantSelectFragmentDoc, UpdateUserFragmentDoc, UpdateProductSelectionFragmentDoc, ChooseProductFragmentDoc, } from "../types";
gql `
  mutation UpdateUser($id: ID!, $userInput: UserInput!) {
    userUpdate(id: $id, userInput: $userInput) {
      ...UpdateUser
    }
  }

  fragment UpdateUser on User {
    phoneNumber
  }

  fragment UpdateUserInput on User {
    ...UpdateUser
    address {
      ...ChooseSubscriptionScreenAddress
    }
  }

  fragment UpdateUserFulfillmentAddress on Fulfillment {
    address {
      ...ChooseSubscriptionScreenAddress
    }
  }
`;
export const useUpdateUser = (currentUserId) => {
    const client = useApolloClient();
    const activeFulfillmentId = useReactiveVar(activeFulfillmentIdVar);
    const saveToFutureFulfillments = useReactiveVar(saveToFutureFulfillmentsVar);
    const [updateUser, result] = useUpdateUserMutation();
    const wrappedFunction = (...args) => {
        const user = client.readFragment({
            id: `User:${currentUserId}`,
            fragment: UpdateUserFragmentDoc,
            fragmentName: "UpdateUser",
        });
        const userInput = {
            phoneNumber: user === null || user === void 0 ? void 0 : user.phoneNumber,
        };
        if (saveToFutureFulfillments) {
            const fulfillmentAddress = client.readFragment({
                id: `Fulfillment:${activeFulfillmentId}`,
                fragment: UpdateUserFulfillmentAddressFragmentDoc,
                fragmentName: "UpdateUserFulfillmentAddress",
            });
            userInput.address = fulfillmentAddress === null || fulfillmentAddress === void 0 ? void 0 : fulfillmentAddress.address;
        }
        return updateUser(Object.assign(Object.assign({}, args), { variables: {
                id: currentUserId,
                userInput,
            } }));
    };
    return { wrapped: wrappedFunction, mutation: updateUser, result: result };
};
gql `
  fragment SaveFulfillmentSettings on Fulfillment {
    id
    fulfillmentDate
    fulfillmentDateAdjustmentReason
    deliveryMethod
    benefit {
      id
    }
    variant {
      ...ChooseSubscriptionScreenVariant
    }
    addons {
      ...ChooseSubscriptionScreenAddon
    }
    address {
      ...ChooseSubscriptionScreenAddress
    }
    productSelections {
      ...ChooseProductSelection
    }
    shippingNotes
    platformLineItems {
      quantity
      platformProductVariantId
      swapped
    }
  }

  fragment OtherUnfulfilledFulfillments on Fulfillment {
    membership {
      unfulfilledFulfillments: fulfillments(query: { isFulfilled: false }) {
        fulfillmentDate
        deliveryMethod
        id
        benefit {
          id
        }
        address {
          ...ChooseSubscriptionScreenAddress
        }
        variant {
          ...ChooseSubscriptionScreenVariant
        }
        addons {
          ...ChooseSubscriptionScreenAddon
        }
        productSelections {
          ...ChooseProductSelection
        }
        shippingNotes
      }
    }
  }
`;
gql `
  mutation UpdateFulfillment(
    $id: ID!
    $variantInput: VariantInput
    $addressInput: AddressInput
    $addons: [AddonInput!]!
    $shippingNotes: String
    $platformLineItems: [PlatformLineItemInput!]
    $productSelections: [ProductSelectionInput!]
    $fulfillmentDate: String
    $fulfillmentDateAdjustmentReason: String
    $deliveryMethod: DeliveryMethod
  ) {
    fulfillmentUpdate(
      id: $id
      fulfillmentInput: {
        addons: $addons
        address: $addressInput
        variant: $variantInput
        shippingNotes: $shippingNotes
        productSelections: $productSelections
        platformLineItems: $platformLineItems
        fulfillmentDate: $fulfillmentDate
        fulfillmentDateAdjustmentReason: $fulfillmentDateAdjustmentReason
        deliveryMethod: $deliveryMethod
      }
    ) {
      id
      deliveryMethod
      addons {
        ...ChooseSubscriptionScreenAddon
      }
      variant {
        ...ChooseSubscriptionScreenVariant
      }
      address {
        ...ChooseSubscriptionScreenAddress
      }
      productSelections {
        ...ChooseProductSelection
      }
      shippingNotes
      fulfillmentDate
      fulfillmentDateAdjustmentReason
    }
  }
`;
export const useSaveFulfillmentSettings = ({ id, includeDate = false }) => {
    const client = useApolloClient();
    const saveToFutureFulfillments = useReactiveVar(saveToFutureFulfillmentsVar);
    const [updateFulfillmentMutation, result] = useUpdateFulfillmentMutation();
    const wrappedFunction = async (...args) => {
        var _a, _b;
        const fulfillmentData = client.readFragment({
            id: `Fulfillment:${id}`,
            fragment: SaveFulfillmentSettingsFragmentDoc,
            fragmentName: "SaveFulfillmentSettings",
        });
        const productSelections = fulfillmentData.productSelections.map(productSelection => {
            var _a;
            return ({
                productId: productSelection.product.id,
                variantId: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id,
            });
        });
        const result = await updateFulfillmentMutation(Object.assign(Object.assign({}, args), { variables: Object.assign({ id: id, variantInput: fulfillmentData.variant, addressInput: fulfillmentData.address, shippingNotes: fulfillmentData.shippingNotes, addons: fulfillmentData.addons, productSelections, platformLineItems: fulfillmentData.platformLineItems, deliveryMethod: fulfillmentData.deliveryMethod }, (includeDate && {
                fulfillmentDate: fulfillmentData.fulfillmentDate,
                fulfillmentDateAdjustmentReason: fulfillmentData.fulfillmentDateAdjustmentReason
            })) }));
        if (result && saveToFutureFulfillments) {
            const otherFulfillments = client.readFragment({
                id: `Fulfillment:${id}`,
                fragment: OtherUnfulfilledFulfillmentsFragmentDoc,
                fragmentName: "OtherUnfulfilledFulfillments",
            });
            if (!otherFulfillments)
                return result;
            let otherFulfillmentIds = otherFulfillments.membership.unfulfilledFulfillments.filter((f) => {
                return new Date(f.fulfillmentDate) >= new Date(fulfillmentData.fulfillmentDate) &&
                    id !== f.id &&
                    f.benefit.id === fulfillmentData.benefit.id;
            }).map(f => f.id);
            for (let otherFufillmentId of otherFulfillmentIds) {
                await updateFulfillmentMutation(Object.assign(Object.assign({}, args), { variables: {
                        id: otherFufillmentId,
                        variantInput: fulfillmentData.variant,
                        addressInput: fulfillmentData.address,
                        shippingNotes: fulfillmentData.shippingNotes,
                        addons: fulfillmentData.addons,
                        productSelections,
                        deliveryMethod: fulfillmentData.deliveryMethod,
                    } }));
            }
        }
        if (result) {
            // Save to past fulfillments with missing information
            const otherFulfillments = client.readFragment({
                id: `Fulfillment:${id}`,
                fragment: OtherUnfulfilledFulfillmentsFragmentDoc,
                fragmentName: "OtherUnfulfilledFulfillments",
            });
            if (!otherFulfillments)
                return result;
            // Addresses
            let missingAddressFulfillments = otherFulfillments.membership.unfulfilledFulfillments.filter((f) => {
                return (!f.address ||
                    !f.address.line1 ||
                    !f.address.city ||
                    !f.address.state ||
                    !f.address.zip ||
                    !f.address.country) && id !== f.id && f.benefit.id === fulfillmentData.benefit.id;
            });
            for (let missingAddressFulfillment of missingAddressFulfillments) {
                const missingProductSelections = missingAddressFulfillment.productSelections.map((productSelection) => {
                    var _a;
                    return {
                        productId: productSelection.product.id,
                        variantId: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id,
                    };
                });
                await updateFulfillmentMutation(Object.assign(Object.assign({}, args), { variables: {
                        id: missingAddressFulfillment.id,
                        variantInput: missingAddressFulfillment.variant,
                        addressInput: fulfillmentData.address,
                        shippingNotes: fulfillmentData.shippingNotes,
                        addons: missingAddressFulfillment.addons,
                        productSelections: missingProductSelections,
                    } }));
            }
            // Variants
            if (!!((_a = fulfillmentData.variant) === null || _a === void 0 ? void 0 : _a.id)) {
                let missingVariantFulfillments = otherFulfillments.membership.unfulfilledFulfillments.filter((f) => {
                    var _a;
                    return (!((_a = f.variant) === null || _a === void 0 ? void 0 : _a.id)) && id !== f.id && f.benefit.id === fulfillmentData.benefit.id;
                });
                for (let missingVariantFulfillment of missingVariantFulfillments) {
                    await updateFulfillmentMutation(Object.assign(Object.assign({}, args), { variables: {
                            id: missingVariantFulfillment.id,
                            variantInput: fulfillmentData.variant,
                            addressInput: missingVariantFulfillment.address,
                            shippingNotes: missingVariantFulfillment.shippingNotes,
                            addons: missingVariantFulfillment.addons,
                        } }));
                }
            }
            // ProductSelections
            else if (!!((_b = fulfillmentData.productSelections) === null || _b === void 0 ? void 0 : _b.length)) {
                let missingProductSelectionFulfillments = otherFulfillments.membership.unfulfilledFulfillments.filter((f) => {
                    const oldProductSelections = f.productSelections.map((productSelection) => {
                        var _a;
                        return {
                            productId: productSelection.product.id,
                            variantId: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id,
                        };
                    });
                    return (oldProductSelections.some((ps) => !ps.variantId) ||
                        productSelections.some((psNew) => !oldProductSelections.some((psOld) => psOld.productId === psNew.productId))) && id !== f.id && f.benefit.id === fulfillmentData.benefit.id;
                });
                for (let missingProductSelectionFulfillment of missingProductSelectionFulfillments) {
                    const oldProductSelections = missingProductSelectionFulfillment.productSelections.map((productSelection) => {
                        var _a;
                        return {
                            productId: productSelection.product.id,
                            variantId: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id,
                        };
                    }).filter((ps) => !!ps.variantId);
                    const oldProductIds = oldProductSelections.map((ps) => ps.productId);
                    const newProductSelections = productSelections.filter((ps) => !oldProductIds.includes(ps.productId));
                    const mergedProductSelections = [...oldProductSelections, ...newProductSelections];
                    await updateFulfillmentMutation(Object.assign(Object.assign({}, args), { variables: {
                            id: missingProductSelectionFulfillment.id,
                            addressInput: missingProductSelectionFulfillment.address,
                            shippingNotes: missingProductSelectionFulfillment.shippingNotes,
                            addons: missingProductSelectionFulfillment.addons,
                            productSelections: mergedProductSelections,
                        } }));
                }
            }
        }
        return result;
    };
    return {
        wrapped: wrappedFunction,
        original: updateFulfillmentMutation,
        result,
    };
};
export const useUpdateVariantSelect = (id) => {
    const client = useApolloClient();
    const updateVariantSelect = (ev) => {
        const variantData = client.readFragment({
            id: `Variant:${ev.target.value}`,
            fragment: ChooseSubscriptionScreenVariantFragmentDoc,
        });
        client.writeFragment({
            fragment: UpdateVariantSelectFragmentDoc,
            fragmentName: "UpdateVariantSelect",
            data: {
                __typename: "Fulfillment",
                id: id,
                variant: variantData,
            },
        });
    };
    return updateVariantSelect;
};
gql `
fragment UpdateProductSelection on Fulfillment {
  productSelections {
    ...ChooseProductSelection
  }
}
`;
export const useUpdateProductSelection = (productId, fulfillmentId) => {
    const client = useApolloClient();
    const updateProductSelection = (ev) => {
        if (!ev.target.value)
            return;
        const fulfillment = client.readFragment({
            id: `Fulfillment:${fulfillmentId}`,
            fragment: UpdateProductSelectionFragmentDoc,
            fragmentName: "UpdateProductSelection",
        });
        const productSelections = fulfillment.productSelections.map(productSelection => {
            if (productSelection.product.id === productId) {
                const variant = productSelection.product.variants.find(v => v.id === ev.target.value);
                return Object.assign(Object.assign({}, productSelection), { variant });
            }
            else {
                return productSelection;
            }
        });
        if (!productSelections.some((productSelection) => productSelection.product.id === productId)) {
            const variant = client.readFragment({
                id: `Variant:${ev.target.value}`,
                fragment: ChooseSubscriptionScreenVariantFragmentDoc,
                fragmentName: "ChooseSubscriptionScreenVariant",
            });
            const product = client.readFragment({
                id: `Product:${productId}`,
                fragment: ChooseProductFragmentDoc,
                fragmentName: "ChooseProduct",
            });
            productSelections.push({
                "__typename": "ProductSelection",
                product,
                variant,
            });
        }
        client.writeFragment({
            id: `Fulfillment:${fulfillmentId}`,
            fragment: UpdateProductSelectionFragmentDoc,
            fragmentName: "UpdateProductSelection",
            data: {
                productSelections,
            }
        });
    };
    return updateProductSelection;
};
